import { Link } from "react-router-dom";
import { ShieldCheck, Users, Leaf, Target, Heart, Award, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, SectionHeading, Overline } from "@/components/Primitives";
import { IMAGES } from "@/data/site";

const VALUES = [
  { icon: ShieldCheck, title: "Licensed & Insured", desc: "State-certified technicians and full liability coverage on every job — your protection is guaranteed." },
  { icon: Users, title: "Experienced Technicians", desc: "Our team averages 8+ years in the field and completes ongoing IPM certification annually." },
  { icon: Leaf, title: "Eco-Friendly Treatments", desc: "We lead with low-toxicity, targeted products that are safe for children, pets, and pollinators." },
];

const MISSION = [
  { icon: Target, title: "Our Mission", desc: "To protect what matters most — your family, your business, and the environment — with honest, effective pest control." },
  { icon: Heart, title: "Our Promise", desc: "Transparent pricing, respectful technicians, and results backed by our re-treatment guarantee." },
  { icon: Award, title: "Our Standard", desc: "Every treatment follows Integrated Pest Management principles for lasting, responsible results." },
];

export default function About() {
  return (
    <div>
      <section className="pt-36 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Overline>Our Story</Overline>
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-brand-green tracking-tight leading-[1.05] mt-4">
              Eco-conscious pest control, built on trust
            </h1>
            <p className="text-brand-muted text-lg leading-relaxed mt-6">
              EXORA Pest Control was founded on a simple belief: protecting your home shouldn't mean harming the world
              around it. What began as a single family-run truck in Surrey has grown into the region's most trusted
              eco-conscious pest control company.
            </p>
            <p className="text-brand-muted leading-relaxed mt-4">
              Today, our licensed technicians serve thousands of homes and businesses with the same care and integrity
              we started with — combining modern integrated pest management with genuine, local service.
            </p>
            <div className="flex gap-8 mt-8">
              {[["12+", "Years Serving"], ["1,200+", "Happy Clients"], ["100%", "Satisfaction Guarantee"]].map(([n, l]) => (
                <div key={l}><p className="font-heading text-3xl font-extrabold text-brand-gold">{n}</p><p className="text-brand-muted text-sm mt-1">{l}</p></div>
              ))}
            </div>
          </div>
          <Reveal>
            <div className="relative">
              <div className="rounded-[2rem] overflow-hidden shadow-xl h-[460px]"><img src={IMAGES.hero} alt="EXORA technician" className="w-full h-full object-cover" /></div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-lg p-5 border border-black/5 max-w-[200px]">
                <div className="flex items-center gap-2"><Leaf className="h-5 w-5 text-brand-green" /><span className="font-heading font-bold text-brand-green">Certified Green</span></div>
                <p className="text-brand-muted text-xs mt-2">EPA-approved, family-safe treatment protocols.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading center overline="Why Trust EXORA" title="Credentials you can count on" />
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="bg-brand-light rounded-3xl p-8 border border-black/5 h-full">
                  <div className="h-14 w-14 rounded-2xl bg-brand-green flex items-center justify-center"><v.icon className="h-7 w-7 text-brand-gold" /></div>
                  <h3 className="font-heading text-xl font-bold text-brand-green mt-6">{v.title}</h3>
                  <p className="text-brand-muted text-sm leading-relaxed mt-3">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="Mission & Values" title="What drives every visit" />
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {MISSION.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.08}>
                <div className="rounded-3xl p-8 border border-black/5 bg-white shadow-sm h-full">
                  <m.icon className="h-8 w-8 text-brand-gold" />
                  <h3 className="font-heading text-xl font-bold text-brand-green mt-5">{m.title}</h3>
                  <p className="text-brand-muted text-sm leading-relaxed mt-3">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 bg-brand-green rounded-[2rem] p-10 lg:p-14 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-white">Ready to experience the EXORA difference?</h3>
              <p className="text-white/70 mt-3">Join thousands of protected homes and businesses across Metro Vancouver.</p>
            </div>
            <div className="flex lg:justify-end">
              <Link to="/request-inspection"><Button className="rounded-full bg-brand-gold text-brand-green font-bold h-14 px-10 hover:bg-brand-goldHover">Book Your Inspection <ArrowRight className="h-5 w-5 ml-2" /></Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
